import "./styles.scss";
import { useHistory } from "react-router-dom";
import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import {
  sendPushAPI,
  getPushHistoryAPI,
  sendPushFromProviderAPI,
  getPushScheduleProvidersAPI,
  getPushScheduleTimezonesAPI,
} from "../api/ClientApi";
import { useDispatch } from "react-redux";
import { handleLogoutRedux } from "../../redux/actions/userAction";
import {
  BellIcon,
  CheckCircleIcon,
  XCircleIcon,
  SendIcon,
  EyeIcon,
  RefreshIcon,
  CloseIcon,
} from "../icons";

const TARGET_LABELS = {
  app: "Tất cả thiết bị",
  user: "User ID",
  device: "Device ID",
  timezone: "Timezone",
};

const stripHtml = (html) => String(html || "").replace(/<[^>]+>/g, "").trim();

const parseLogData = (raw) => {
  if (!raw) return null;
  try {
    return JSON.parse(raw);
  } catch (e) {
    return null;
  }
};

const TARGET_OPTIONS = [
  { value: "app", label: "Tất cả thiết bị" },
  { value: "user", label: "Theo User ID" },
  { value: "device", label: "Theo Device ID" },
  { value: "timezone", label: "Theo Timezone" },
];

const CONTENT_SOURCE_OPTIONS = [
  { value: "manual", label: "Nhập tay" },
  { value: "provider", label: "Lấy từ provider" },
];

const ENVIRONMENT_OPTIONS = [
  { value: "production", label: "Production" },
  { value: "sandbox", label: "Sandbox" },
];

const ENVIRONMENT_LABELS = {
  production: "Production",
  sandbox: "Sandbox",
};

const HISTORY_PAGE_SIZE = 10;

const ChipGroup = ({ options, value, onChange, ariaLabel }) => (
  <div className="push-chips" role="group" aria-label={ariaLabel}>
    {options.map((option) => (
      <button
        key={option.value}
        type="button"
        className={`push-chip${value === option.value ? " is-active" : ""}`}
        onClick={() => onChange(option.value)}
      >
        {option.label}
      </button>
    ))}
  </div>
);

const PushNotification = (props) => {
  const [appId, setAppId] = useState("");
  const [contentSource, setContentSource] = useState("manual");
  const [providerKey, setProviderKey] = useState("");
  const [providers, setProviders] = useState([]);
  const [timezones, setTimezones] = useState([]);
  const [target, setTarget] = useState("app");
  const [targetValue, setTargetValue] = useState("");
  const [environment, setEnvironment] = useState("production");
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [dataJson, setDataJson] = useState("");
  const [history, setHistory] = useState([]);
  const [reload, setReload] = useState(true);
  const [selectedLog, setSelectedLog] = useState(null);
  const [resendingKey, setResendingKey] = useState(null);
  const [sending, setSending] = useState(false);
  const [historyPage, setHistoryPage] = useState(1);

  const historyPush = useHistory();
  const dispatch = useDispatch();

  useEffect(() => {
    const app = props.location.state;
    if (!app) {
      historyPush.push("/admin");
      return;
    }
    setAppId(app.app_id);
    getPushScheduleProvidersAPI().then((response) => {
      if (response.errorCode === 0) {
        setProviders(response.data || []);
      }
    });
    getPushScheduleTimezonesAPI({ appId: app.app_id }).then((response) => {
      if (response.errorCode === 0) {
        setTimezones(response.data || []);
      }
    });
  }, []);

  useEffect(() => {
    if (reload && appId) {
      getPushHistoryAPI({ appId }).then((response) => {
        setReload(false);
        const { errorCode, message, data } = response;
        if (errorCode === 0) {
          setHistory(data);
          setHistoryPage(1);
        } else if (errorCode === 401) {
          dispatch(handleLogoutRedux());
        } else {
          toast.error(message);
        }
      });
    }
  }, [reload, appId, dispatch]);

  const invalidInputs = () => {
    if (contentSource === "manual") {
      if (!title) {
        toast.error("Title is not empty!");
        return false;
      }
      if (!body) {
        toast.error("Body is not empty!");
        return false;
      }
      if (dataJson) {
        try {
          JSON.parse(dataJson);
        } catch (e) {
          toast.error("Data must be a valid JSON!");
          return false;
        }
      }
    } else if (!providerKey) {
      toast.error("Chọn 1 provider!");
      return false;
    }
    if (target !== "app" && !targetValue) {
      toast.error("Target value is not empty!");
      return false;
    }
    return true;
  };

  const handleSend = () => {
    if (!invalidInputs()) {
      return;
    }
    setSending(true);
    const request =
      contentSource === "manual"
        ? sendPushAPI({
            appId,
            target,
            targetValue,
            title,
            body,
            data: dataJson ? JSON.parse(dataJson) : undefined,
            environment,
          })
        : sendPushFromProviderAPI({ appId, providerKey, target, targetValue, environment });
    request
      .then((response) => {
        const { errorCode, message, data } = response;
        if (errorCode === 0) {
          const successCount = data.successCount || 0;
          const failureCount = data.failureCount || 0;
          const fetchedTitle = data.payload ? stripHtml(data.payload.title) : null;
          const prefix = fetchedTitle ? `Đã lấy "${fetchedTitle}" và gửi: ` : "Đã gửi: ";
          if (successCount === 0 && failureCount === 0) {
            toast.warning(
              "Chưa có thiết bị nào đăng ký nhận push cho target này — chưa gửi được cho ai. Thiết bị cần gọi /push/register-token trước."
            );
          } else if (successCount === 0) {
            toast.error(
              `Gửi thất bại toàn bộ (${failureCount} thiết bị). Kiểm tra lại Firebase credential của app hoặc token thiết bị.`
            );
          } else {
            toast.success(`${prefix}${successCount} thành công, ${failureCount} thất bại.`);
          }
          if (contentSource === "manual") {
            setTitle("");
            setBody("");
            setDataJson("");
          }
        } else if (errorCode === 401) {
          toast.error(message);
          dispatch(handleLogoutRedux());
        } else {
          toast.error(message);
        }
        setReload(true);
      })
      .finally(() => setSending(false));
  };

  // Gửi lại đúng title/body/data/target/environment đã log — dùng chung /push/send, kể cả
  // log gốc do PushScheduleEngine gửi theo timezone (xem pushScheduleEngine.js +
  // pushController.sendPush). Dùng lại environment đã log (không phải lựa chọn hiện tại của
  // form) để tránh gửi nhầm qua project khác với lần gửi gốc.
  const handleResend = (item, key) => {
    const resendTarget = item.target_type;
    const resendTargetValue = resendTarget === "app" ? undefined : item.target_value;
    setResendingKey(key);
    sendPushAPI({
      appId,
      target: resendTarget,
      targetValue: resendTargetValue,
      title: item.title,
      body: item.body,
      data: parseLogData(item.data) || undefined,
      environment: item.environment || "production",
    })
      .then((response) => {
        const { errorCode, message, data } = response;
        if (errorCode === 0) {
          const successCount = data.successCount || 0;
          const failureCount = data.failureCount || 0;
          toast.success(`Đã gửi lại: ${successCount} thành công, ${failureCount} thất bại.`);
          setSelectedLog(null);
          setReload(true);
        } else if (errorCode === 401) {
          toast.error(message);
          dispatch(handleLogoutRedux());
        } else {
          toast.error(message);
        }
      })
      .finally(() => setResendingKey(null));
  };

  const totalSent = history.length;
  const totalSuccess = history.reduce((sum, item) => sum + (item.success_count || 0), 0);
  const totalFailure = history.reduce((sum, item) => sum + (item.failure_count || 0), 0);
  const totalHistoryPages = Math.max(1, Math.ceil(history.length / HISTORY_PAGE_SIZE));
  const currentHistoryPage = Math.min(historyPage, totalHistoryPages);
  const pagedHistory = history.slice(
    (currentHistoryPage - 1) * HISTORY_PAGE_SIZE,
    currentHistoryPage * HISTORY_PAGE_SIZE
  );

  const previewTitle =
    contentSource === "manual"
      ? title || "Tiêu đề thông báo"
      : providerKey
        ? `Từ provider: ${providerKey}`
        : "Chọn provider để xem nội dung khi gửi";
  const previewBody =
    contentSource === "manual"
      ? body || "Nội dung body sẽ hiện ở đây…"
      : "Nội dung lấy từ provider lúc gửi.";

  return (
    <div className="page-container push-page">
      <div className="page-header">
        <div>
          <h1 className="page-title">Push Notification</h1>
          <p className="page-subtitle">{appId}</p>
        </div>
        <button
          type="button"
          className="btn btn-outline-secondary"
          onClick={() => historyPush.push("/admin")}
        >
          ← Quay lại App Manager
        </button>
      </div>

      <div className="stat-grid">
        <div className="stat-card">
          <div className="stat-card-icon">
            <BellIcon />
          </div>
          <div>
            <div className="stat-card-value">{totalSent}</div>
            <div className="stat-card-label">Lượt gửi</div>
          </div>
        </div>
        <div className="stat-card stat-card--success">
          <div className="stat-card-icon">
            <CheckCircleIcon />
          </div>
          <div>
            <div className="stat-card-value">{totalSuccess}</div>
            <div className="stat-card-label">Thành công (cộng dồn)</div>
          </div>
        </div>
        <div className="stat-card stat-card--danger">
          <div className="stat-card-icon">
            <XCircleIcon />
          </div>
          <div>
            <div className="stat-card-value">{totalFailure}</div>
            <div className="stat-card-label">Thất bại (cộng dồn)</div>
          </div>
        </div>
      </div>

      <div className="push-compose">
        <div className="push-compose__form">
          <div className="push-panel">
            <div className="form-section-title">Cấu hình gửi</div>

            <div className="mb-3">
              <label className="form-label">Môi trường gửi</label>
              <ChipGroup
                ariaLabel="Môi trường gửi"
                options={ENVIRONMENT_OPTIONS}
                value={environment}
                onChange={setEnvironment}
              />
              <div className="form-text">
                Sandbox dùng project Firebase riêng (nếu app đã cấu hình) — an toàn để test trước
                khi gửi thật.
              </div>
            </div>

            <div className="mb-3">
              <label className="form-label">Nguồn nội dung</label>
              <ChipGroup
                ariaLabel="Nguồn nội dung"
                options={CONTENT_SOURCE_OPTIONS}
                value={contentSource}
                onChange={setContentSource}
              />
            </div>

            {contentSource === "provider" && (
              <div className="mb-3">
                <label className="form-label">Provider</label>
                <select
                  className="form-select"
                  value={providerKey}
                  onChange={(event) => setProviderKey(event.target.value)}
                >
                  <option value="">-- Chọn provider --</option>
                  {providers.map((key) => (
                    <option key={key} value={key}>
                      {key}
                    </option>
                  ))}
                </select>
                <div className="form-text">
                  Nội dung push sẽ được tự động lấy từ nguồn API tương ứng ngay khi bấm gửi
                  (giống logic PushSchedule dùng cho lịch tự động).
                </div>
              </div>
            )}

            <div className="mb-3">
              <label className="form-label">Gửi tới</label>
              <ChipGroup
                ariaLabel="Đối tượng gửi"
                options={TARGET_OPTIONS}
                value={target}
                onChange={(value) => {
                  setTarget(value);
                  setTargetValue("");
                }}
              />
            </div>

            {target === "timezone" ? (
              <div className="mb-3">
                <label className="form-label">Timezone</label>
                {timezones.length > 0 ? (
                  <select
                    className="form-select"
                    value={targetValue}
                    onChange={(event) => setTargetValue(event.target.value)}
                  >
                    <option value="">-- Chọn timezone --</option>
                    {timezones.map((tz) => (
                      <option key={tz} value={tz}>
                        {tz}
                      </option>
                    ))}
                  </select>
                ) : (
                  <input
                    className="form-control"
                    type="text"
                    placeholder="VD: Asia/Ho_Chi_Minh"
                    value={targetValue}
                    onChange={(event) => setTargetValue(event.target.value)}
                  />
                )}
              </div>
            ) : (
              target !== "app" && (
                <div className="mb-3">
                  <label className="form-label">
                    {target === "user" ? "User ID" : "Device ID"}
                  </label>
                  <input
                    className="form-control"
                    type="text"
                    placeholder={target === "user" ? "User ID" : "Device ID"}
                    value={targetValue}
                    onChange={(event) => setTargetValue(event.target.value)}
                  />
                </div>
              )
            )}

            {contentSource === "manual" && (
              <>
                <div className="form-section-title">Nội dung</div>
                <div className="mb-3">
                  <label className="form-label">Title</label>
                  <input
                    className="form-control"
                    type="text"
                    placeholder="Tiêu đề thông báo"
                    value={title}
                    onChange={(event) => setTitle(event.target.value)}
                  />
                </div>
                <div className="mb-3">
                  <label className="form-label">Body</label>
                  <textarea
                    className="form-control"
                    placeholder="Nội dung thông báo"
                    rows={3}
                    value={body}
                    onChange={(event) => setBody(event.target.value)}
                  />
                </div>
                <div className="mb-3">
                  <label className="form-label">Data (optional JSON)</label>
                  <textarea
                    className="form-control"
                    placeholder='Ví dụ: {"screen":"home"}'
                    rows={3}
                    value={dataJson}
                    onChange={(event) => setDataJson(event.target.value)}
                  />
                </div>
              </>
            )}

            <button
              className="btn btn-primary d-flex align-items-center gap-2"
              disabled={sending}
              onClick={() => handleSend()}
            >
              <SendIcon width={16} height={16} />
              {sending ? "Đang gửi..." : "Gửi thông báo"}
            </button>
          </div>
        </div>

        <aside className="push-compose__preview">
          <div className="push-panel push-preview">
            <div className="form-section-title">Xem trước</div>
            <div className="push-preview__device">
              <div className="push-preview__notch" />
              <div className="push-preview__banner">
                <div className="push-preview__app">
                  <span className="push-preview__dot" />
                  <span>{appId || "App"}</span>
                  <span className="push-preview__now">now</span>
                </div>
                <div className="push-preview__title">{previewTitle}</div>
                <div className="push-preview__body">{previewBody}</div>
              </div>
            </div>
            <div className="push-preview__meta">
              <span
                className={`badge rounded-pill text-bg-${
                  environment === "sandbox" ? "warning" : "dark"
                }`}
              >
                {ENVIRONMENT_LABELS[environment]}
              </span>
              <span className="badge rounded-pill text-bg-secondary">
                {TARGET_LABELS[target] || target}
              </span>
              {target !== "app" && targetValue && (
                <span className="push-preview__target-value">{targetValue}</span>
              )}
            </div>
          </div>
        </aside>
      </div>

      <div className="push-history">
        <div className="push-history__header">
          <h2 className="push-history__title">Lịch sử gửi</h2>
          <span className="text-muted small">{history.length} bản tin</span>
        </div>

        <div className="push-history__list">
          {pagedHistory.map((item, index) => {
            const key = `${item.createdAt}-${index}`;
            const hasFailure = (item.failure_count || 0) > 0;
            return (
              <div key={key} className="push-row">
                <div className="push-row__main">
                  <div className="push-row__title-line">
                    <span className="push-row__title">{item.title || "(không tiêu đề)"}</span>
                    <span
                      className={`badge rounded-pill text-bg-${
                        item.environment === "sandbox" ? "warning" : "dark"
                      }`}
                    >
                      {ENVIRONMENT_LABELS[item.environment] || "Production"}
                    </span>
                    <span className="badge rounded-pill text-bg-secondary">
                      {TARGET_LABELS[item.target_type] || item.target_type}
                    </span>
                  </div>
                  <div className="push-row__body">{item.body}</div>
                  <div className="push-row__sub">
                    <span>{new Date(item.createdAt).toLocaleString()}</span>
                    {item.target_value && <span>· {item.target_value}</span>}
                  </div>
                  {item.error && <div className="push-row__error">{item.error}</div>}
                </div>
                <div className="push-row__side">
                  <div className="push-row__counts">
                    <span className="badge text-bg-success">{item.success_count} OK</span>
                    <span className={`badge text-bg-${hasFailure ? "danger" : "secondary"}`}>
                      {item.failure_count} fail
                    </span>
                  </div>
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-secondary d-flex align-items-center gap-1"
                    onClick={() => setSelectedLog(item)}
                  >
                    <EyeIcon width={14} height={14} />
                    Chi tiết
                  </button>
                </div>
              </div>
            );
          })}
        </div>

        {history.length === 0 && (
          <div className="empty-state">Chưa gửi thông báo nào cho app này.</div>
        )}

        {history.length > 0 && (
          <div className="push-history__pager">
            <span className="text-muted small">
              {(currentHistoryPage - 1) * HISTORY_PAGE_SIZE + 1}–
              {Math.min(currentHistoryPage * HISTORY_PAGE_SIZE, history.length)} /{" "}
              {history.length} bản tin
            </span>
            <div className="d-flex align-items-center gap-2">
              <button
                type="button"
                className="btn btn-sm btn-outline-secondary"
                disabled={currentHistoryPage <= 1}
                onClick={() => setHistoryPage((p) => Math.max(1, p - 1))}
              >
                ‹ Trước
              </button>
              <span className="small text-muted">
                Trang {currentHistoryPage} / {totalHistoryPages}
              </span>
              <button
                type="button"
                className="btn btn-sm btn-outline-secondary"
                disabled={currentHistoryPage >= totalHistoryPages}
                onClick={() => setHistoryPage((p) => Math.min(totalHistoryPages, p + 1))}
              >
                Sau ›
              </button>
            </div>
          </div>
        )}
      </div>

      {selectedLog && (
        <div
          className="modal fade show d-block push-detail-backdrop"
          tabIndex={-1}
          role="dialog"
          onClick={() => setSelectedLog(null)}
        >
          <div
            className="modal-dialog modal-dialog-centered modal-lg"
            role="document"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="modal-content push-detail-modal">
              <div className="modal-header">
                <h5 className="modal-title">Chi tiết thông báo</h5>
                <button
                  type="button"
                  className="btn-close-custom"
                  aria-label="Close"
                  onClick={() => setSelectedLog(null)}
                >
                  <CloseIcon width={18} height={18} />
                </button>
              </div>
              <div className="modal-body">
                <dl className="detail-list">
                  <dt>Thời gian</dt>
                  <dd>{new Date(selectedLog.createdAt).toLocaleString()}</dd>
                  <dt>Môi trường</dt>
                  <dd>
                    <span
                      className={`badge rounded-pill text-bg-${
                        selectedLog.environment === "sandbox" ? "warning" : "dark"
                      }`}
                    >
                      {ENVIRONMENT_LABELS[selectedLog.environment] || "Production"}
                    </span>
                  </dd>
                  <dt>Target</dt>
                  <dd>
                    <span className="badge rounded-pill text-bg-secondary">
                      {TARGET_LABELS[selectedLog.target_type] || selectedLog.target_type}
                    </span>{" "}
                    {selectedLog.target_value}
                  </dd>
                  <dt>Title</dt>
                  <dd>{selectedLog.title}</dd>
                  <dt>Body</dt>
                  <dd className="text-break">{selectedLog.body}</dd>
                  <dt>Data (payload)</dt>
                  <dd>
                    <pre className="detail-json">
                      {parseLogData(selectedLog.data)
                        ? JSON.stringify(parseLogData(selectedLog.data), null, 2)
                        : selectedLog.data || "—"}
                    </pre>
                  </dd>
                  <dt>Kết quả</dt>
                  <dd>
                    <span className="badge text-bg-success me-2">
                      {selectedLog.success_count} thành công
                    </span>
                    <span className="badge text-bg-danger">
                      {selectedLog.failure_count} thất bại
                    </span>
                  </dd>
                  {selectedLog.error && (
                    <>
                      <dt>Lỗi</dt>
                      <dd className="text-danger">{selectedLog.error}</dd>
                    </>
                  )}
                </dl>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={() => setSelectedLog(null)}
                >
                  Đóng
                </button>
                <button
                  type="button"
                  className="btn btn-primary d-flex align-items-center gap-2"
                  disabled={resendingKey === "modal"}
                  onClick={() => handleResend(selectedLog, "modal")}
                >
                  <RefreshIcon width={16} height={16} />
                  {resendingKey === "modal" ? "Đang gửi..." : "Gửi lại thông báo này"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PushNotification;
